import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";
import userLocation from "../../images/user_location_icon.svg";
import { getElevationAtDistanceKm, exponentialSmoothingAlpha, smoothLngLat } from "./trailPreviewMath";

// Relive-style flyover: the route line draws at a CONSTANT speed while the camera follows the head at a
// fixed zoom, slowly orbiting; a flat-2D -> 3D intro plays first and a 2D zoom-out plays at the end.
// Speed is ms per km of trail (lower = faster). The mobile apps use ~2.2 s/km at 1x.
const SPEED_CONFIG = {
  slow: { durationPerKm: 4400 },
  medium: { durationPerKm: 2200 },
  fast: { durationPerKm: 1100 },
  ultrafast: { durationPerKm: 700 },
  hyperspeed: { durationPerKm: 400 }
};

const FLYOVER_ZOOM = 14;     // constant follow zoom
const FLYOVER_PITCH = 45;    // top-down-ish tilt (route reads clearly, incl. mountains)
const ORBIT_DEGREES = 180;   // total slow orbit over the whole flyover
const INTRO_MS = 2000;       // flat-2D -> 3D tilt
const OUTRO_MS = 2500;       // zoom-out to the whole route in 2D
// Time constant (s) for easing the camera centre toward the head. The head moves piecewise-linearly
// along the route, so its pan direction snaps at every vertex; smoothing the centre rounds those
// corners (the marker + drawn line stay on the true head). Larger = smoother but more head lag.
const CENTER_SMOOTH_TAU = 0.35;
const MAX_FRAME_SECONDS = 0.1; // clamp dt so a tab stall can't make the centre lurch on resume

/**
 * Relive-style trail flyover. Returns a controller:
 * pause(), resume(), stop(), isPaused(), fastForward(), rewind(), getProgress(),
 * getEndState(), getStartState(), setSpeedMultiplier(), getSpeedMultiplier(), then(), destroy().
 */
export function previewTrail(
  map,
  trailCoords,
  mode = "medium",
  markerOptions = {},
  progressCallback,
  cameraOptions = {}
) {
  const config = SPEED_CONFIG[mode] || SPEED_CONFIG.medium;

  const coords3d = trailCoords.map(c => (c.length > 2 ? [c[0], c[1], c[2] || 0] : [c[0], c[1], 0]));
  const coords2d = coords3d.map(c => [c[0], c[1]]);
  const line = turf.lineString(coords2d);
  const totalKm = turf.length(line, { units: "kilometers" });
  const { cumulativeDistKm } = computeCumulativeDistances(coords3d);
  const startCoord = coords2d[0];

  // Fixed orbit-base bearing: start -> farthest point (robust for loops; captures the main extent).
  let far = startCoord;
  let farthest = 0;
  for (const c of coords2d) {
    const d = turf.distance(turf.point(startCoord), turf.point(c), { units: "kilometers" });
    if (d > farthest) { farthest = d; far = c; }
  }
  const routeBearing = farthest > 0.02 ? turf.bearing(turf.point(startCoord), turf.point(far)) : 0;

  // Progress line layer (blue, draws behind the head).
  if (!map.getSource("progressTrail")) {
    map.addSource("progressTrail", { type: "geojson", data: turf.lineString([startCoord, startCoord]) });
    map.addLayer({
      id: "progressTrail",
      type: "line",
      source: "progressTrail",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#2cc2e3", "line-width": 5 }
    });
  }

  // Position puck at the head of the progress line.
  const markerElement = document.createElement("div");
  markerElement.className = "custom-marker";
  markerElement.style.backgroundImage = `url(${userLocation})`;
  markerElement.style.width = markerOptions.width || "28px";
  markerElement.style.height = markerOptions.height || "28px";
  markerElement.style.backgroundSize = "contain";
  markerElement.style.backgroundRepeat = "no-repeat";
  markerElement.style.backgroundPosition = "center";
  const marker = new mapboxgl.Marker({
    element: markerElement,
    rotationAlignment: markerOptions.rotationAlignment || "map",
    pitchAlignment: markerOptions.pitchAlignment || "auto",
    offset: markerOptions.offset || [0, 0]
  })
    .setLngLat(startCoord)
    .addTo(map);

  return animateReliveFlyover({
    map,
    marker,
    line,
    coords3d,
    cumulativeDistKm,
    totalKm,
    routeBearing,
    duration: totalKm * config.durationPerKm,
    progressCallback
  });
}

// ----------------------------- Animation -----------------------------

function animateReliveFlyover(opts) {
  const { map, marker, line, coords3d, cumulativeDistKm, totalKm, routeBearing, duration, progressCallback } = opts;
  const startCoord = line.geometry.coordinates[0];

  let currentT = 0;
  let speedMultiplier = 1;
  let lastFrameTime = 0;
  let requestId = null;
  let started = false;
  let paused = false;
  let finished = false;
  let destroyed = false;
  let smoothedCenter = null; // eased camera centre; trails the head to round corners
  let resolveFunc;
  const promise = new Promise(resolve => { resolveFunc = resolve; });

  // Render the camera + line + marker for a normalized progress t.
  // When animating we ease the camera centre toward the head (dtSeconds > 0); for discrete jumps
  // (intro hand-off, fast-forward/rewind) we snap the centre straight onto the head.
  function renderAt(t, snap = true, dtSeconds = 0) {
    const distKm = totalKm * t;
    const head = turf.along(line, distKm, { units: "kilometers" }).geometry.coordinates;
    const bearing = routeBearing + t * ORBIT_DEGREES;

    if (snap || !smoothedCenter) {
      smoothedCenter = [head[0], head[1]];
    } else {
      const alpha = exponentialSmoothingAlpha(dtSeconds, CENTER_SMOOTH_TAU);
      smoothedCenter = smoothLngLat(smoothedCenter, head, alpha);
    }

    map.jumpTo({ center: smoothedCenter, zoom: FLYOVER_ZOOM, bearing, pitch: FLYOVER_PITCH });
    marker.setLngLat(head);

    const src = map.getSource("progressTrail");
    if (src) {
      const drawn = distKm <= 0.0005
        ? [startCoord, startCoord]
        : turf.lineSliceAlong(line, 0, distKm, { units: "kilometers" }).geometry.coordinates;
      src.setData(turf.lineString(drawn.length >= 2 ? drawn : [startCoord, startCoord]));
    }

    if (progressCallback) {
      progressCallback(distKm, getElevationAtDistanceKm(distKm, coords3d, cumulativeDistKm));
    }
  }

  function frame() {
    if (paused || finished) { requestId = null; return; }
    const now = performance.now();
    const elapsedMs = now - lastFrameTime;
    const dt = duration > 0 ? (elapsedMs / duration) * speedMultiplier : 1;
    lastFrameTime = now;
    currentT = Math.min(currentT + dt, 1);
    renderAt(currentT, false, Math.min(elapsedMs / 1000, MAX_FRAME_SECONDS));
    if (currentT < 1) {
      requestId = requestAnimationFrame(frame);
    } else {
      finished = true;
      playOutro();
      resolveFunc();
    }
  }

  function startFlyover() {
    if (started || destroyed) return;
    started = true;
    lastFrameTime = performance.now();
    requestId = requestAnimationFrame(frame);
  }

  // Flat 2D top-down (north) -> tilt up into the 3D flyover orientation, then start the flyover.
  function playIntro() {
    map.jumpTo({ center: startCoord, zoom: FLYOVER_ZOOM, bearing: 0, pitch: 0 });
    map.easeTo({ center: startCoord, zoom: FLYOVER_ZOOM, bearing: routeBearing, pitch: FLYOVER_PITCH, duration: INTRO_MS });
    // Timer-based handoff (robust regardless of moveend firing).
    setTimeout(() => startFlyover(), INTRO_MS + 50);
  }

  // On completion: smoothly zoom out to a top-down 2D view of the whole route.
  function playOutro() {
    const bounds = new mapboxgl.LngLatBounds();
    line.geometry.coordinates.forEach(c => bounds.extend(c));
    const cam = map.cameraForBounds(bounds, { padding: 60, bearing: 0 });
    if (cam) {
      map.easeTo({ center: cam.center, zoom: cam.zoom, bearing: 0, pitch: 0, duration: OUTRO_MS });
    }
  }

  playIntro();

  return {
    pause() {
      paused = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
    },
    resume() {
      if (finished || !paused) return;
      paused = false;
      lastFrameTime = performance.now();
      if (started && !requestId) requestId = requestAnimationFrame(frame);
    },
    stop() {
      if (finished) return;
      finished = true;
      paused = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
      resolveFunc();
    },
    isPaused() { return paused; },
    fastForward() {
      currentT = Math.min(currentT + (totalKm > 0 ? 1 / totalKm : 0.05), 1);
      renderAt(currentT);
    },
    rewind() {
      currentT = Math.max(currentT - (totalKm > 0 ? 1 / totalKm : 0.05), 0);
      renderAt(currentT);
    },
    getProgress() { return currentT; },
    getEndState() { return { bearing: routeBearing, pitch: FLYOVER_PITCH }; },
    getStartState() { return { bearing: routeBearing, pitch: FLYOVER_PITCH }; },
    setSpeedMultiplier(nextSpeed) {
      if (typeof nextSpeed === "number" && nextSpeed > 0) speedMultiplier = nextSpeed;
    },
    getSpeedMultiplier() { return speedMultiplier; },
    then(onFulfilled, onRejected) { return promise.then(onFulfilled, onRejected); },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      paused = true;
      finished = true;
      if (requestId) { cancelAnimationFrame(requestId); requestId = null; }
      marker.remove();
      resolveFunc();
    }
  };
}

// ----------------------------- Helpers -----------------------------

function computeCumulativeDistances(coords3d) {
  let total = 0;
  const cumulativeDistKm = [0];
  for (let i = 1; i < coords3d.length; i++) {
    const seg = turf.distance(
      turf.point([coords3d[i - 1][0], coords3d[i - 1][1]]),
      turf.point([coords3d[i][0], coords3d[i][1]]),
      { units: "kilometers" }
    );
    total += seg;
    cumulativeDistKm.push(total);
  }
  return { cumulativeDistKm };
}
